import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import Ico from './ui/Ico';

function parseCSVLine(line) {
  const values = [];
  let current = '';
  let insideQuotes = false;

  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    const nextChar = line[index + 1];

    if (char === '"' && insideQuotes && nextChar === '"') {
      current += '"';
      index += 1;
      continue;
    }

    if (char === '"') {
      insideQuotes = !insideQuotes;
      continue;
    }

    if (char === ',' && !insideQuotes) {
      values.push(current);
      current = '';
      continue;
    }

    current += char;
  }

  values.push(current);
  return values.map(value => value.trim());
}

export default function BulkUpload({ api }) {
  const [funnels, setFunnels] = useState([]);
  const [selectedFunnel, setSelectedFunnel] = useState('');
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    fetchFunnels();
  }, []);

  const fetchFunnels = async () => {
    try {
      const response = await api.get('/funnels/list');
      setFunnels(response.data);
    } catch (error) {
      toast.error('Erro ao carregar funis');
    }
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
      const lowerName = selectedFile.name.toLowerCase();
      const isExcel = lowerName.endsWith('.xls') || lowerName.endsWith('.xlsx');

      if (!validTypes.includes(selectedFile.type) && !lowerName.endsWith('.csv') && !isExcel) {
        toast.error('Formato de arquivo não suportado. Use CSV ou Excel.');
        return;
      }

      setFile(selectedFile);
      parsePreview(selectedFile).catch(() => {
        if (isExcel) {
          toast.error('Não foi possível ler o arquivo Excel. Verifique se o formato é válido ou envie em CSV.');
        } else {
          toast.error('Não foi possível ler o arquivo. Verifique se o formato é válido.');
        }
        setFile(null);
        document.getElementById('file-input').value = '';
      });
    }
  };

  const parseSpreadsheetFile = async (file) => {
    const lowerName = file.name.toLowerCase();

    if (lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls')) {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      if (!firstSheetName) throw new Error('Planilha vazia');
      const sheet = workbook.Sheets[firstSheetName];
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
      return rows
        .map(row => Array.from(row).map(cell => String(cell).trim()))
        .filter(row => row.some(cell => cell !== ''));
    }

    const text = await file.text();
    const rows = text
      .replace(/^\uFEFF/, '')
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(Boolean)
      .map(parseCSVLine);

    return rows.filter(row => row.some(cell => String(cell).trim() !== ''));
  };

  const parsePreview = async (file) => {
    const rows = await parseSpreadsheetFile(file);

    if (rows.length > 1) {
      setPreview({
        headers: rows[0].map(cell => String(cell)),
        data: rows.slice(1, 5).map(row => row.map(cell => String(cell))),
        totalRows: rows.length - 1
      });
    }
  };

  const rowsToLeads = (rows) => {
    const headers = rows[0].map(h => String(h).toLowerCase());
    const leads = [];

    for (let i = 1; i < rows.length; i++) {
      const values = rows[i].map(cell => String(cell).trim());
      const lead = {};

      headers.forEach((header, index) => {
        const value = values[index];
        if (!value) return;

        if (header.includes('name') || header.includes('nome')) {
          lead.name = value;
        } else if (header.includes('email') || header.includes('e-mail')) {
          lead.email = value;
        } else if (header.includes('phone') || header.includes('telefone') || header.includes('celular')) {
          lead.phone = value;
        } else if (header.includes('company') || header.includes('empresa')) {
          lead.company = value;
        } else if (header.includes('note') || header.includes('observa')) {
          lead.notes = value;
        }
      });

      if (lead.name && lead.email) {
        leads.push(lead);
      }
    }

    return leads;
  };

  const handleUpload = async () => {
    if (!file || !selectedFunnel) {
      toast.error('Selecione um arquivo e um funil');
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      let rows;
      try {
        rows = await parseSpreadsheetFile(file);
      } catch (error) {
        if (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')) {
          toast.error('Não foi possível ler o arquivo Excel. Verifique se o formato é válido ou envie em CSV.');
        } else {
          toast.error('Não foi possível ler o arquivo. Verifique se o formato é válido.');
        }
        return;
      }

      const leads = rowsToLeads(rows);

      if (leads.length === 0) {
        toast.error('Nenhum lead válido encontrado no arquivo');
        return;
      }

      try {
        const response = await api.post('/leads/bulk-upload', {
          leads: leads,
          funnel_id: selectedFunnel,
          source: 'bulk_upload'
        });

        setUploadResult(response.data);

        if (response.data.created_count > 0) {
          toast.success(`${response.data.created_count} leads importados com sucesso!`);
        }

        if (response.data.error_count > 0) {
          toast.warning(`${response.data.error_count} leads tiveram problemas`);
        }

      } catch (error) {
        const message = error.response?.data?.detail || 'Erro ao fazer upload dos leads';
        toast.error(message);
      }

    } catch (error) {
      toast.error('Erro ao processar arquivo');
    } finally {
      setUploading(false);
    }
  };

  const downloadSampleCSV = () => {
    const sampleData = `name,email,phone,company,notes
João Silva,joao@empresa.com,(11) 99999-9999,Empresa ABC,Cliente em potencial
Maria Santos,maria@exemplo.com,(11) 88888-8888,Exemplo Ltda,Interessada em produto X
Pedro Costa,pedro@negocio.com,(11) 77777-7777,Negócio Inc,Contato via LinkedIn`;

    const blob = new Blob([sampleData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'exemplo-leads.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const resetUpload = () => {
    setFile(null);
    setPreview(null);
    setUploadResult(null);
    setSelectedFunnel('');
    document.getElementById('file-input').value = '';
  };

  const getFunnelName = (funnelId) => {
    const funnel = funnels.find(f => f.id === funnelId);
    return funnel ? funnel.name : 'Desconhecido';
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="bulk-upload-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Upload em Massa</h1>
          <p className="text-slate-600 mt-1">Importe leads em lote via CSV ou Excel</p>
        </div>
        <Button
          variant="outline"
          onClick={downloadSampleCSV}
          className="bg-white/60"
          data-testid="download-sample-button"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
          </svg>
          Baixar Exemplo CSV
        </Button>
      </div>

      {/* Instructions */}
      <Card className="glass border-white/20">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Como Fazer o Upload</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-slate-800 mb-3"><Ico e="📋" />Campos Obrigatórios</h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li>• <strong>name/nome:</strong> Nome completo do lead</li>
                <li>• <strong>email:</strong> Endereço de email válido</li>
              </ul>
              
              <h4 className="font-semibold text-slate-800 mb-3 mt-4"><Ico e="📋" />Campos Opcionais</h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li>• <strong>phone/telefone:</strong> Número de contato</li>
                <li>• <strong>company/empresa:</strong> Nome da empresa</li>
                <li>• <strong>notes/observações:</strong> Anotações adicionais</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-slate-800 mb-3"><Ico e="✅" />Formatos Aceitos</h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li>• CSV (Comma Separated Values)</li>
                <li>• Excel (.xls, .xlsx)</li>
              </ul>
              
              <h4 className="font-semibold text-slate-800 mb-3 mt-4"><Ico e="⚠" />Importante</h4>
              <ul className="space-y-2 text-sm text-slate-600">
                <li>• Emails duplicados serão ignorados</li>
                <li>• Máximo 1000 leads por upload</li>
                <li>• Primeira linha deve conter cabeçalhos</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Upload Form */}
      <Card className="glass border-white/20">
        <CardHeader>
          <CardTitle>Importar Leads</CardTitle>
          <CardDescription>Selecione o funil de destino e faça o upload do arquivo</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Funnel Selection */}
          <div className="space-y-2">
            <Label htmlFor="funnel-select">Funil de Destino *</Label>
            <Select value={selectedFunnel} onValueChange={setSelectedFunnel}>
              <SelectTrigger data-testid="funnel-select">
                <SelectValue placeholder="Selecione o funil onde os leads serão adicionados" />
              </SelectTrigger>
              <SelectContent>
                {funnels.map(funnel => (
                  <SelectItem key={funnel.id} value={funnel.id}>
                    {funnel.name} - {funnel.type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <Label htmlFor="file-input">Arquivo CSV/Excel *</Label>
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
              <input
                id="file-input"
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
                data-testid="file-input"
              />
              
              {!file ? (
                <div>
                  <svg className="w-12 h-12 text-slate-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-slate-600 mb-2">Clique para selecionar ou arraste o arquivo aqui</p>
                  <p className="text-sm text-slate-500">CSV, XLS, XLSX até 5MB</p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('file-input').click()}
                    className="mt-4"
                  >
                    Selecionar Arquivo
                  </Button>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-center space-x-2 text-green-600 mb-2">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-medium">{file.name}</span>
                  </div>
                  <p className="text-sm text-slate-500 mb-4">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('file-input').click()}
                    size="sm"
                  >
                    Alterar Arquivo
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Preview */}
          {preview && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-semibold text-slate-800">Prévia do Arquivo</h4>
                <Badge variant="secondary">
                  {preview.totalRows} linhas detectadas
                </Badge>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full border border-slate-200 rounded-lg">
                  <thead className="bg-slate-50">
                    <tr>
                      {preview.headers.map((header, index) => (
                        <th key={index} className="px-4 py-2 text-left text-sm font-medium text-slate-700 border-b">
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.data.map((row, rowIndex) => (
                      <tr key={rowIndex} className="border-b border-slate-100">
                        {row.map((cell, cellIndex) => (
                          <td key={cellIndex} className="px-4 py-2 text-sm text-slate-600">
                            {cell || '-'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {preview.totalRows > 4 && (
                <p className="text-sm text-slate-500 text-center">
                  ... e mais {preview.totalRows - 4} linhas
                </p>
              )}
            </div>
          )}

          {/* Upload Button */}
          <div className="flex items-center space-x-4">
            <Button
              onClick={handleUpload}
              disabled={!file || !selectedFunnel || uploading}
              className="btn-primary"
              data-testid="upload-button"
            >
              {uploading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Fazendo Upload...</span>
                </div>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 12l3-3m0 0l3 3m-3-3v9" />
                  </svg>
                  Fazer Upload
                </>
              )}
            </Button>

            {(file || uploadResult) && (
              <Button
                variant="outline"
                onClick={resetUpload}
                disabled={uploading}
              >
                Limpar
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Upload Result */}
      {uploadResult && (
        <Card className="glass border-white/20">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Resultado do Upload</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-600">{uploadResult.created_count}</div>
                <div className="text-sm text-slate-600">Leads Criados</div>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-red-600">{uploadResult.error_count}</div>
                <div className="text-sm text-slate-600">Erros</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <div className="text-sm text-slate-600">Funil de Destino</div>
                <div className="font-bold text-blue-600">{getFunnelName(selectedFunnel)}</div>
              </div>
            </div>

            {uploadResult.errors && uploadResult.errors.length > 0 && (
              <Alert>
                <AlertDescription>
                  <strong>Erros encontrados:</strong>
                  <ul className="mt-2 list-disc list-inside text-sm">
                    {uploadResult.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
